import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  FaUserCircle,
  FaQrcode,
  FaTimes,
  FaCamera,
  FaDownload,
  FaCopy,
  FaShareAlt,
  FaEye,
  FaEyeSlash,
  FaIdBadge,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaCheckCircle,
} from "react-icons/fa";
import { QRCodeSVG } from "qrcode.react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import usePageTitle from "../hooks/usePageTitle";
import { createPortal } from "react-dom";

/* ----------------------------- Toast (minimal) ----------------------------- */
const Toast = ({ kind = "info", message, onClose }) => {
  const bg =
    kind === "success"
      ? "bg-emerald-600/90"
      : kind === "error"
      ? "bg-rose-600/90"
      : "bg-indigo-600/90";
  return (
    <div
      className={`fixed left-1/2 -translate-x-1/2 bottom-5 z-[999999] ${bg} text-slate-100 px-5 py-3 rounded-lg shadow-xl flex items-center gap-3 backdrop-blur-md`}
      role="status"
      aria-live="polite"
    >
      <span className="text-sm">{message}</span>
      <button onClick={onClose} className="opacity-80 hover:opacity-100" aria-label="Dismiss">
        ✕
      </button>
    </div>
  );
};

/* --------------------------------- Page ---------------------------------- */
export default function MyProfile() {
  /* -------------------------- Load user (local) -------------------------- */
  const initialUser = JSON.parse(localStorage.getItem("user")) || {
    name: "Unknown User",
    role: "Member",
    address: "—",
    contactno: "—",
    image: "/user.png",
    SLIITID: "",
  };
  const [user, setUser] = useState(initialUser);

  /* ------------------------------ UI helpers ----------------------------- */
  const [toast, setToast] = useState(null);
  const [toastKind, setToastKind] = useState("info");
  const showToast = (msg, kind = "info", ms = 1600) => {
    setToastKind(kind);
    setToast(msg);
    setTimeout(() => setToast(null), ms);
  };

  /* ------------------------------ Profile form --------------------------- */
  const [profile, setProfile] = useState({
    name: initialUser.name || "",
    Address: initialUser.address || "",
    ContactNo: initialUser.contactno || "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  /* ------------------------------- QR Modal ------------------------------ */
  const [showQRModal, setShowQRModal] = useState(false);
  const qrBoxRef = useRef(null);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && setShowQRModal(false);
    if (showQRModal) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onEsc);
    } else {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    }
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [showQRModal]);

  const downloadQR = useCallback(() => {
    if (!qrBoxRef.current) return;
    const svg = qrBoxRef.current.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const dataUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
    const img = new Image();
    const size = 720;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0b1120";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const png = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = png;
      a.download = `${user.SLIITID || "member"}-qr.png`;
      a.click();
    };
    img.src = dataUrl;
  }, [user.SLIITID]);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(user.SLIITID || "");
      showToast("ID copied", "success", 1200);
    } catch {
      showToast("Copy failed", "error");
    }
  };

  const shareQR = async () => {
    const text = `Member: ${user.name || "User"}\nID: ${user.SLIITID || "N/A"}\nRole: ${user.role || "Member"}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Member QR", text });
      } catch {
        /* cancelled */
      }
    } else {
      showToast("Share not supported on this device");
    }
  };

  /* ----------------------------- Image Upload ---------------------------- */
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const openFilePicker = () => fileInputRef.current?.click();

  const onFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) return showToast("Max file size is 5MB", "error");
    setSelectedImage(file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
  };

  useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl]);

  const handleImageUpload = async () => {
    if (!selectedImage) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("image", selectedImage);
      if (user?.SLIITID) fd.append("SLIITID", user.SLIITID);

      const res = await fetch("https://dev.sliitleo.org/Backend/upload_profile_image.php", {
        method: "POST",
        body: fd,
      });

      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const out = await res.json().catch(() => null);
      if (!out?.success || !out?.url) throw new Error(out?.message || "Upload failed");

      const publicUrl = out.url.startsWith("http") ? out.url : `https://dev.sliitleo.org${out.url}`;

      // Persist (best effort)
      await fetch("https://dev.sliitleo.org/Backend/update_profile.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          SLIITID: user?.SLIITID,
          Image: publicUrl,
          FullName: user?.name ?? "",
          Address: user?.address ?? "",
          ContactNo: user?.contactno ?? "",
        }),
      }).catch(() => {});

      const next = { ...user, image: publicUrl };
      localStorage.setItem("user", JSON.stringify(next));
      setUser(next);
      setSelectedImage(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      showToast("Photo updated", "success");
    } catch (e) {
      showToast(e.message || "Upload error", "error");
    } finally {
      setUploading(false);
    }
  };

  /* --------------------------- Password handling ------------------------- */
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const checks = {
    len: passwordData.newPassword.length >= 8,
    uc: /[A-Z]/.test(passwordData.newPassword),
    num: /\d/.test(passwordData.newPassword),
    sym: /[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword),
    match:
      passwordData.newPassword.length > 0 &&
      passwordData.newPassword === passwordData.confirmPassword,
  };
  const strong = checks.len && checks.uc && checks.num && checks.sym;

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((p) => ({ ...p, [name]: value }));
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!strong || !checks.match) {
      return setPasswordError("Please meet all requirements and confirm the same password.");
    }
    try {
      setIsUpdating(true);
      const apiUrl = new URL("https://dev.sliitleo.org/Backend/change_password.php");
      apiUrl.searchParams.append("_", Date.now());

      const rsp = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          // intentionally no X-Requested-With (avoids CORS block)
        },
        body: JSON.stringify({
          SLIITID: user?.SLIITID,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      const result = await rsp.json().catch(() => ({}));
      if (result?.success) {
        setPasswordSuccess("Password updated");
        setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      } else {
        throw new Error(result?.message || "Update failed");
      }
    } catch (err) {
      setPasswordError(err.message || "Unknown error");
    } finally {
      setIsUpdating(false);
    }
  };

  /* --------------------------- Load profile info -------------------------- */
  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("user"));
    if (!u?.SLIITID) return;
    setLoadingProfile(true);
    fetch("https://dev.sliitleo.org/Backend/get_profile.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ SLIITID: u.SLIITID }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d?.success && d.profile) {
          setProfile({
            name: d.profile.name || u.name || "",
            Address: d.profile.Address || u.address || "",
            ContactNo: d.profile.ContactNo || u.contactno || "",
          });
          if (d.profile.image) {
            const next = { ...u, image: d.profile.image };
            localStorage.setItem("user", JSON.stringify(next));
            setUser(next);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoadingProfile(false));
  }, []);

  const saveProfile = async () => {
    try {
      setSavingProfile(true);
      // Persist to backend (best effort)
      await fetch("https://dev.sliitleo.org/Backend/update_profile.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          SLIITID: user?.SLIITID,
          FullName: profile.name,
          Address: profile.Address,
          ContactNo: profile.ContactNo,
          Image: user?.image || "",
        }),
      }).catch(() => {});
      const nextLocal = {
        ...user,
        name: profile.name,
        address: profile.Address,
        contactno: profile.ContactNo,
      };
      localStorage.setItem("user", JSON.stringify(nextLocal));
      setUser(nextLocal);
      showToast("Profile saved", "success");
    } catch {
      showToast("Unable to save", "error");
    } finally {
      setSavingProfile(false);
    }
  };

  /* ---------------------------------- UI --------------------------------- */
  return (
    <div className="flex bg-[#0b1120] text-slate-200 min-h-screen">
      <Sidebar className="hidden md:block" />
      <Topbar user={user} />

      <main className="md:ml-64 mt-14 w-full px-3 sm:px-6 md:px-10 py-6 md:py-10 relative">
        {/* Ambient glows */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-24 -left-24 w-80 h-80 bg-indigo-500/15 blur-3xl rounded-full" />
          <div className="absolute -bottom-24 -right-24 w-80 h-80 bg-cyan-500/15 blur-3xl rounded-full" />
        </div>

        {/* Header — forced inline CSS, dark navy, premium */}
<motion.div
  initial={{ opacity: 0, y: -6 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.45 }}
  style={{
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.10)',
    background: 'rgba(15,21,40,0.80)',
    backdropFilter: 'blur(12px)',
    padding: 16,
    marginBottom: 24,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    boxShadow: '0 16px 50px -22px rgba(0,0,0,.7)'
  }}
>
  <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <span
      style={{
        background: 'rgba(79,70,229,.90)',
        padding: 8,
        borderRadius: 999,
        boxShadow: '0 10px 24px -12px rgba(49,46,129,.6)'
      }}
    >
      <FaUserCircle style={{ color: '#fff', fontSize: 22 }} />
    </span>
    <span
      style={{
        fontSize: 18,
        fontWeight: 600,
        letterSpacing: 0.2,
        color: '#e8f0ff',
        fontFamily: '"Inter","SF Pro Text",system-ui,-apple-system,Segoe UI,Roboto,sans-serif'
      }}
    >
      Profile
    </span>
  </span>

  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
    <span
      style={{
        padding: '6px 10px',
        borderRadius: 999,
        fontSize: 12,
        background: 'rgba(255,255,255,.06)',
        color: '#e6ecff',
        border: '1px solid rgba(255,255,255,.10)'
      }}
    >
      ID: {user.SLIITID || 'N/A'}
    </span>

    <button
      onClick={() => setShowQRModal(true)}
      title="Show QR Code"
      aria-haspopup="dialog"
      aria-expanded={showQRModal}
      onMouseEnter={(e) => (e.currentTarget.style.background = '#4f46e5')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(79,70,229,.90)')}
      style={{
        background: 'rgba(79,70,229,.90)',
        color: '#fff',
        padding: 8,
        borderRadius: 999,
        border: '1px solid rgba(255,255,255,.10)',
        cursor: 'pointer',
        transition: 'background .15s ease'
      }}
    >
      <FaQrcode style={{ fontSize: 16 }} />
    </button>
  </div>
</motion.div>


        {/* Main grid */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        >
          {/* Left: Avatar & Facts — Upgraded */}
<section
  style={{
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(15,21,40,0.85)",
    backdropFilter: "blur(12px)",
    padding: 20,
    boxShadow: "0 16px 50px -22px rgba(0,0,0,.7)",
  }}
>
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
    {/* Avatar */}
    <div style={{ position: "relative", width: 140, height: 140 }}>
      <img
        src={previewUrl || user.image || "/user.png"}
        onError={(e) => (e.currentTarget.src = "/user.png")}
        alt="Profile"
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          objectFit: "cover",
          border: "4px solid rgba(79,70,229,0.7)",
          boxShadow: "0 6px 20px rgba(0,0,0,.6)",
        }}
      />
      {/* Upload Overlay */}
      <button
        onClick={openFilePicker}
        aria-label="Change photo"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: "rgba(0,0,0,0.55)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: 0,
          transition: "opacity .3s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
      >
        <div style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 500, display: "flex", gap: 6 }}>
          <FaCamera /> Change
        </div>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onFileInputChange}
      />

      {/* Org logo dot (top-left) */}
      <div
        style={{
          position: "absolute",
          top: -6,
          left: -6,
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: "#0f1e55",
          border: "2px solid #0f1e55",
          overflow: "hidden",
        }}
      >
        <img src="/club.png" alt="Org" style={{ width: "100%", height: "100%" }} />
      </div>

      {/* Active green dot (bottom-right) */}
      <div
        style={{
          position: "absolute",
          bottom: -6,
          right: -6,
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "#10b981",
          border: "2px solid #0f1e55",
          display: "grid",
          placeItems: "center",
          boxShadow: "0 0 12px rgba(16,185,129,.9)",
        }}
      >
        <FaCheckCircle style={{ color: "#fff", fontSize: 12 }} />
      </div>
    </div>

    <p style={{ marginTop: 10, fontSize: 12, color: "#94a3b8" }}>PNG/JPG · Max 5MB</p>

    {/* Upload / Remove */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, width: "100%", marginTop: 12 }}>
      <button
        onClick={handleImageUpload}
        disabled={!selectedImage || uploading}
        style={{
          padding: "8px 12px",
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 500,
          background: !selectedImage || uploading ? "rgba(5,150,105,0.4)" : "#059669e6",
          color: "#fff",
          cursor: !selectedImage || uploading ? "not-allowed" : "pointer",
          transition: "background .2s",
        }}
      >
        {uploading ? "Uploading…" : "Upload"}
      </button>
      <button
        onClick={() => {
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setSelectedImage(null);
          setPreviewUrl(null);
        }}
        disabled={!selectedImage && !previewUrl}
        style={{
          padding: "8px 12px",
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 500,
          background: !selectedImage && !previewUrl ? "rgba(220,38,38,0.4)" : "#dc2626e6",
          color: "#fff",
          cursor: !selectedImage && !previewUrl ? "not-allowed" : "pointer",
          transition: "background .2s",
        }}
      >
        Remove
      </button>
    </div>

    {/* Quick facts */}
    <div style={{ width: "100%", marginTop: 24, display: "flex", flexDirection: "column", gap: 10 }}>
      {/* ID */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "rgba(255,255,255,.05)",
          border: "1px solid rgba(255,255,255,.1)",
          borderRadius: 12,
          padding: "8px 12px",
        }}
      >
        <FaIdBadge style={{ color: "rgba(203,213,225,.9)" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>SLIIT ID</div>
          <div style={{ fontSize: 13 }}>{user.SLIITID || "N/A"}</div>
        </div>
        <button
          onClick={copyId}
          style={{
            fontSize: 11,
            padding: "4px 8px",
            borderRadius: 6,
            background: "rgba(255,255,255,.05)",
            border: "1px solid rgba(255,255,255,.1)",
            color: "#e2e8f0",
          }}
        >
          Copy
        </button>
      </div>

      {/* Mobile */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "rgba(255,255,255,.05)",
          border: "1px solid rgba(255,255,255,.1)",
          borderRadius: 12,
          padding: "8px 12px",
        }}
      >
        <FaPhoneAlt style={{ color: "rgba(203,213,225,.9)" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>Mobile</div>
          <div style={{ fontSize: 13 }}>{profile.ContactNo || "—"}</div>
        </div>
      </div>

      {/* Address */}
      <div
        style={{
          display: "flex",
          gap: 10,
          background: "rgba(255,255,255,.05)",
          border: "1px solid rgba(255,255,255,.1)",
          borderRadius: 12,
          padding: "8px 12px",
        }}
      >
        <FaMapMarkerAlt style={{ marginTop: 3, color: "rgba(203,213,225,.9)" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>Address</div>
          <div style={{ fontSize: 13, wordBreak: "break-word" }}>{profile.Address || "—"}</div>
        </div>
      </div>
    </div>

    {/* QR Actions */}
    <div style={{ width: "100%", marginTop: 24, display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
      <button
        onClick={() => setShowQRModal(true)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          padding: "8px 12px",
          borderRadius: 10,
          background: "#4f46e5e6",
          color: "#fff",
          fontSize: 13,
        }}
      >
        <FaQrcode /> QR
      </button>
      <button
        onClick={downloadQR}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          padding: "8px 12px",
          borderRadius: 10,
          background: "rgba(255,255,255,.05)",
          border: "1px solid rgba(255,255,255,.1)",
          color: "#e2e8f0",
          fontSize: 13,
        }}
      >
        <FaDownload /> PNG
      </button>
      <button
        onClick={shareQR}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          padding: "8px 12px",
          borderRadius: 10,
          background: "rgba(255,255,255,.05)",
          border: "1px solid rgba(255,255,255,.1)",
          color: "#e2e8f0",
          fontSize: 13,
        }}
      >
        <FaShareAlt /> Share
      </button>
    </div>
  </div>
</section>


          {/* Right: Forms */}
          <section className="lg:col-span-2 space-y-6">
            {/* Personal info */}
            <div
  style={{
    background: "#0f1528",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "20px",
    boxShadow: "0 16px 40px -22px rgba(0,0,0,.65)",
  }}
>
  <div className="flex items-center justify-between mb-4">
    <div>
      <h2 style={{ fontSize: "16px", fontWeight: 600, color: "#fff" }}>
        Personal Information
      </h2>
      <p style={{ fontSize: "12px", color: "#94a3b8" }}>
        Update your basic details below.
      </p>
    </div>
    {savingProfile ? (
      <span style={{ fontSize: "12px", color: "#94a3b8" }}>Saving…</span>
    ) : (
      <span
        style={{
          fontSize: "12px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          color: "#34d399",
        }}
      >
        <FaCheckCircle /> Synced locally
      </span>
    )}
  </div>

  {/* Name */}
  <label style={{ fontSize: "13px", color: "#cbd5e1" }}>Name</label>
  <input
    type="text"
    style={{
      width: "100%",
      background: "#0d1222",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: "8px",
      padding: "12px",
      color: "#f1f5f9",
      marginTop: "6px",
      marginBottom: "12px",
    }}
    value={profile.name}
    onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
    placeholder="Your full name"
  />

  {/* Mobile */}
  <label style={{ fontSize: "13px", color: "#cbd5e1" }}>Mobile</label>
  <input
    type="tel"
    style={{
      width: "100%",
      background: "#0d1222",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: "8px",
      padding: "12px",
      color: "#f1f5f9",
      marginTop: "6px",
      marginBottom: "12px",
    }}
    value={profile.ContactNo}
    onChange={(e) => setProfile((p) => ({ ...p, ContactNo: e.target.value }))}
    placeholder="07XXXXXXXX"
  />

  {/* Address */}
  <label style={{ fontSize: "13px", color: "#cbd5e1" }}>Address</label>
  <input
    type="text"
    style={{
      width: "100%",
      background: "#0d1222",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: "8px",
      padding: "12px",
      color: "#f1f5f9",
      marginTop: "6px",
      marginBottom: "16px",
    }}
    value={profile.Address}
    onChange={(e) => setProfile((p) => ({ ...p, Address: e.target.value }))}
    placeholder="Street, City"
  />

  {/* Save Changes button (inside Personal Info card) */}
<div className="md:col-span-2 flex justify-end mt-4">
  <button
    type="submit"
    disabled={savingProfile}
    className={`w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 
      rounded-lg font-medium text-white transition-all
      ${
        savingProfile
          ? "bg-indigo-700/70 cursor-not-allowed"
          : "bg-indigo-600/90 hover:bg-indigo-700 shadow-md shadow-indigo-900/40"
      }`}
  >
    {savingProfile ? "Saving…" : "Save Changes"}
  </button>
</div>

</div>


            {/* Password */}
            <div
  style={{
    background: "#0f1528",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "20px",
    boxShadow: "0 16px 40px -22px rgba(0,0,0,.65)",
    marginTop: "24px",
  }}
>
  <h2 style={{ fontSize: "16px", fontWeight: 600, color: "#fff" }}>
    Change Password
  </h2>
  <p style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "12px" }}>
    Create a strong, unique password.
  </p>

  {/* Current Password */}
  <label style={{ fontSize: "13px", color: "#cbd5e1" }}>Current password</label>
  <input
    type="password"
    style={{
      width: "100%",
      background: "#0d1222",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: "8px",
      padding: "12px",
      color: "#f1f5f9",
      marginTop: "6px",
      marginBottom: "12px",
    }}
    placeholder="••••••••"
  />

  {/* New Password */}
  <label style={{ fontSize: "13px", color: "#cbd5e1" }}>New password</label>
  <input
    type="password"
    style={{
      width: "100%",
      background: "#0d1222",
      border: strong ? "1px solid #34d399" : "1px solid #f59e0b",
      borderRadius: "8px",
      padding: "12px",
      color: "#f1f5f9",
      marginTop: "6px",
      marginBottom: "12px",
    }}
    placeholder="Create a strong password"
  />

  {/* Confirm */}
  <label style={{ fontSize: "13px", color: "#cbd5e1" }}>Confirm password</label>
  <input
    type="password"
    style={{
      width: "100%",
      background: "#0d1222",
      border: checks?.match
        ? "1px solid #34d399"
        : "1px solid #f87171",
      borderRadius: "8px",
      padding: "12px",
      color: "#f1f5f9",
      marginTop: "6px",
      marginBottom: "16px",
    }}
    placeholder="Re-enter password"
  />

  {/* Rules */}
  <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "12px" }}>
    • Min 8 chars • Uppercase • Number • Special character
  </div>

  {/* Update Button */}
  {/* Update Password button (inside Password card) */}
<div className="md:col-span-2 flex items-center justify-between mt-4">
  <div className="text-[11px] text-slate-400">
    • At least 8 chars • Uppercase • Number • Special character
  </div>
  <button
    type="submit"
    disabled={isUpdating || !strong || !checks.match}
    className={`w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 
      rounded-lg font-medium text-white transition-all
      ${
        isUpdating || !strong || !checks.match
          ? "bg-indigo-700/50 cursor-not-allowed"
          : "bg-indigo-600/90 hover:bg-indigo-700 shadow-md shadow-indigo-900/40"
      }`}
  >
    {isUpdating ? "Updating…" : "Update Password"}
  </button>
</div>


  {/* Messages */}
  {passwordSuccess && (
    <p style={{ color: "#34d399", fontSize: "13px", marginTop: "10px" }}>
      {passwordSuccess}
    </p>
  )}
  {passwordError && (
    <p style={{ color: "#f87171", fontSize: "13px", marginTop: "10px" }}>
      {passwordError}
    </p>
  )}
</div>

          </section>
        </motion.div>

        {/* QR Code Modal */}
        {showQRModal &&
          createPortal(
            <div
              className="fixed inset-0 z-[2147483647] flex items-center justify-center p-4"
              onClick={() => setShowQRModal(false)}
              aria-modal="true"
              role="dialog"
              aria-labelledby="qrModalTitle"
              style={{ backgroundColor: "rgba(3,6,16,0.92)" }}
            >
              <div
                className="w-full max-w-md rounded-2xl border shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                style={{ backgroundColor: "#0b1120", borderColor: "rgba(255,255,255,0.08)" }}
              >
                {/* Header */}
                <div
                  className="flex items-center justify-between px-6 py-4"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <h3 id="qrModalTitle" className="text-base font-semibold text-slate-100">
                    Member QR Code
                  </h3>
                  <button
                    onClick={() => setShowQRModal(false)}
                    className="text-slate-300 hover:text-slate-100 transition-colors"
                    aria-label="Close"
                  >
                    <FaTimes className="text-lg" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-6 flex flex-col items-center">
                  <div
                    ref={qrBoxRef}
                    className="p-4 rounded-xl ring-1 ring-white/10 shadow-inner bg-[#0b1120]"
                  >
                    <QRCodeSVG
                      value={JSON.stringify({
                        SLIITID: user.SLIITID || user.id || "",
                        name: user.name || "User",
                        role: user.role || "Member",
                        contact: user.contactno || "N/A",
                      })}
                      size={220}
                      level="H"
                      includeMargin
                      bgColor="#0b1120"
                      fgColor="#e5e7eb"
                    />
                  </div>

                  <h4 className="mt-4 text-base font-semibold text-slate-100">{user.name || "User"}</h4>
                  <p className="text-slate-300">ID: {user.SLIITID || "N/A"}</p>
                  <p className="text-slate-300">Role: {user.role || "Member"}</p>
                  {user.contactno && <p className="text-sm text-slate-400">Contact: {user.contactno}</p>}

                  <div className="mt-5 grid grid-cols-3 gap-3 w-full">
                    <button
                      onClick={downloadQR}
                      className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-indigo-600/90 hover:bg-indigo-700 text-white text-sm"
                    >
                      <FaDownload /> Download
                    </button>
                    <button
                      onClick={copyId}
                      className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-100 text-sm"
                    >
                      <FaCopy /> Copy ID
                    </button>
                    <button
                      onClick={shareQR}
                      className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-600/90 hover:bg-emerald-600 text-white text-sm"
                    >
                      <FaShareAlt /> Share
                    </button>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 pb-6">
                  <button
                    onClick={() => setShowQRModal(false)}
                    className="w-full px-6 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-slate-100"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

        {toast && <Toast kind={toastKind} message={toast} onClose={() => setToast(null)} />}
      </main>
    </div>
  );
}
